import { useEffect, useRef, type ReactNode } from "react";

// Native modal dialog with built-in backdrop, focus trap, Escape key

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Maximum width - defaults to a comfortable sm. */
  maxWidth?: "sm" | "md" | "lg";
  /** When true, Escape and backdrop-click are suppressed (e.g. an operation is in flight). */
  preventClose?: boolean;
  "data-testid"?: string;
}

const MAX_WIDTHS: Record<NonNullable<DialogProps["maxWidth"]>, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

export function Dialog({ open, onClose, title, children, maxWidth = "sm", preventClose = false, "data-testid": testId }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  // Sync React state with native dialog close/cancel events
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => onClose();
    el.addEventListener("close", handler);
    return () => el.removeEventListener("close", handler);
  }, [onClose]);

  // Suppress native Escape while close is prevented. The dialog's `cancel` event is
  // cancelable; preventDefault stops the browser from closing it (and thus from firing
  // `close`), so React state and the native dialog stay in sync.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onCancel = (e: Event) => {
      if (preventClose) e.preventDefault();
    };
    el.addEventListener("cancel", onCancel);
    return () => el.removeEventListener("cancel", onCancel);
  }, [preventClose]);

  return (
    <dialog
      ref={ref}
      onClick={(e) => {
        // Close on backdrop click (target is the dialog itself, not content)
        if (e.target === ref.current && !preventClose) onClose();
      }}
      className={`fixed inset-0 m-auto h-fit max-h-[calc(100vh-2rem)] ${MAX_WIDTHS[maxWidth]} w-full rounded border border-ink/5 bg-surface p-0 shadow-xl backdrop:bg-ink/40 backdrop:backdrop-blur-md`}
      data-testid={testId}
    >
      <div className="p-6">
        {title ? (
          <h2 className="mb-4 text-lg font-semibold">{title}</h2>
        ) : null}
        {children}
      </div>
    </dialog>
  );
}
